import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AutoAwesome as AutoFilledIcon,
  ExpandLess,
  ExpandMore,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Inventory2 as PickListIcon,
  InfoOutlined as InfoIcon,
  Key as KeyIcon,
  Link as LinkIcon,
  PrecisionManufacturing as AutoGeneratedIcon,
  PushPin as FixedIcon,
  Person as OperatorIcon,
} from '@mui/icons-material';

import {
  canonicalizeKnownColumnType,
  columnTypeSelectOptions,
} from '../schema-manager/schemaColumnDataTypes';

/** Extract enum values from a column's typed validation_rules.
 *  Phase 3 emits ``type='enum', values=[...]``; pre-Phase-3 recipes
 *  may still carry ``rule='enum', allowed_values=[...]``. Read both. */
function columnEnumValues(column: any): string[] | null {
  const rules = column?.validation_rules;
  if (!Array.isArray(rules)) return null;
  for (const r of rules) {
    if (!r || typeof r !== 'object') continue;
    const kind = r.type ?? r.rule;
    if (kind !== 'enum') continue;
    const vals = Array.isArray(r.values) && r.values.length > 0
      ? r.values
      : Array.isArray(r.allowed_values) && r.allowed_values.length > 0
        ? r.allowed_values
        : null;
    if (vals) return vals.map(String);
  }
  return null;
}

/** Extract range bounds from a column's typed validation_rules.
 *  Returns ``{min, max}`` when a range rule is present, ``null`` otherwise. */
function columnRangeBounds(column: any): { min: number; max: number } | null {
  const rules = column?.validation_rules;
  if (!Array.isArray(rules)) return null;
  for (const r of rules) {
    if (!r || typeof r !== 'object') continue;
    const kind = r.type ?? r.rule;
    if (kind !== 'range') continue;
    if (r.min == null && r.max == null) continue;
    return { min: Number(r.min ?? -Infinity), max: Number(r.max ?? Infinity) };
  }
  return null;
}

/** Format one validation rule into a single readable line for tooltips.
 *  Accepts both Phase 3 typed (``{type, values, ...}``) and legacy
 *  (``{rule, allowed_values, ...}``) shapes. */
function formatRuleLine(rule: any): string {
  if (typeof rule === 'string') return rule;
  if (!rule || typeof rule !== 'object') return String(rule);
  const parts: string[] = [];
  const kind = rule.type ?? rule.rule;
  if (kind) parts.push(String(kind));
  if (rule.message) parts.push(String(rule.message));
  const values =
    (Array.isArray(rule.values) && rule.values.length > 0 && rule.values) ||
    (Array.isArray(rule.allowed_values) && rule.allowed_values.length > 0 && rule.allowed_values) ||
    null;
  if (values) {
    parts.push(`Values: ${values.join(', ')}`);
  }
  if (rule.pattern) parts.push(`Pattern: ${rule.pattern}`);
  if (rule.min !== undefined || rule.max !== undefined) {
    parts.push(`Range: ${rule.min ?? ''}–${rule.max ?? ''}`);
  }
  return parts.length > 0 ? parts.join(' — ') : JSON.stringify(rule);
}

const coerceBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'y', 'on'].includes(v)) return true;
    if (['false', '0', 'no', 'n', 'off', ''].includes(v)) return false;
  }
  return undefined;
};

export interface ColumnRowWithCatalogProps {
  /** Column dict from `definition.tables[name].columns[i]`. */
  column: any;
  /** Whether this column is the level's key column. Shown as a chip. */
  isLevelKey?: boolean;
  /** View or edit. */
  mode: 'view' | 'edit';
  /** Edit-mode column mutator. `field` is the dotted key on the column
   *  dict (e.g. `description`, `type`, `fixed`). */
  onColumnPatch?: (field: string, value: any) => void;
  /** Edit-mode: open the validation rules dialog. */
  onOpenRulesEditor?: () => void;
  onRemoveColumn?: () => void;
}

/**
 * One column row plus an inline `<Collapse>` body showing the column's
 * closed-domain enum values (when ``validation_rules`` declares
 * ``type='enum'``). Phase 3 retired the standalone value-catalog
 * construct; closed-domain constraints now live inline on the column's
 * ``validation_rules`` and render directly from there.
 *
 * The structural data carried on the column (PK, FK, NN,
 * is_platform_synthesized, is_ancestor_identity, fixed, derivation,
 * scope_parent) all render as chips with plain-language tooltips —
 * never via string parsing of `description`.
 */
const ColumnRowWithCatalog: React.FC<ColumnRowWithCatalogProps> = ({
  column,
  isLevelKey,
  mode,
  onColumnPatch,
  onOpenRulesEditor,
  onRemoveColumn,
}) => {
  const isEdit = mode === 'edit';
  const colName: string = column?.name || '';

  const description: string = column?.description || '';
  const fixedValue: string | null =
    column?.fixed != null && column.fixed !== '' ? String(column.fixed) : null;
  const requiresOperatorValue =
    coerceBoolean(column?.requires_operator_value) === true;
  const isAutoGenerated = coerceBoolean(column?.is_platform_synthesized) === true;
  const isInherited = coerceBoolean(column?.is_ancestor_identity) === true;
  // FK rendering: ``is_foreign_key=true`` + ``references="<table>.<col>"``
  // is the canonical channel both the parser (parent_link stamping) and
  // the INTERPRET LLM (``annotate_foreign_keys``) write to. We display
  // either the parsed target ("Refers to: <table>") or a generic chip
  // when the references string is missing/malformed.
  const isForeignKey = coerceBoolean(column?.is_foreign_key) === true;
  const referencesRaw: string | null =
    typeof column?.references === 'string' && column.references.trim()
      ? column.references.trim()
      : null;
  const referencesParsed: { table: string; column: string } | null = (() => {
    if (!referencesRaw) return null;
    const idx = referencesRaw.lastIndexOf('.');
    if (idx <= 0 || idx === referencesRaw.length - 1) return null;
    return {
      table: referencesRaw.slice(0, idx),
      column: referencesRaw.slice(idx + 1),
    };
  })();
  const derivation = column?.derivation || null;
  const scopeParent: string | null = column?.scope_parent || null;
  const hasPrimaryKey =
    coerceBoolean(column?.primary_key) === true ||
    coerceBoolean(column?.is_primary_key) === true;
  const hasUnique =
    coerceBoolean(column?.unique) === true ||
    coerceBoolean(column?.is_unique) === true;
  const nullableState =
    coerceBoolean(column?.nullable) ?? coerceBoolean(column?.is_nullable);

  const enumValues = columnEnumValues(column);
  const rangeBounds = columnRangeBounds(column);

  const [expanded, setExpanded] = useState(false);
  const [draftName, setDraftName] = useState(colName);
  const canExpand = enumValues !== null && enumValues.length > 0;

  useEffect(() => {
    setDraftName(colName);
  }, [colName]);

  const ancestor = column?.ancestor_element;
  const ancestorSrc = column?.ancestor_identity_source;

  return (
    <>
      <TableRow hover>
        {/* Expand chevron */}
        <TableCell padding="none" sx={{ width: 40, pl: 1 }}>
          {canExpand && (
            <IconButton size="small" onClick={() => setExpanded((v) => !v)}>
              {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
          {isEdit && onRemoveColumn && (
            <IconButton size="small" onClick={onRemoveColumn} aria-label="Remove column">
              <DeleteIcon fontSize="small" />
            </IconButton>
          )}
        </TableCell>

        {/* Name */}
        <TableCell>
          {isEdit ? (
            <TextField
              size="small"
              variant="standard"
              value={draftName}
              onChange={(e) => setDraftName(e.target.value)}
              onBlur={() => {
                if (draftName !== colName) {
                  onColumnPatch?.('name', draftName);
                }
              }}
              fullWidth
              InputProps={{ sx: { fontWeight: 500, fontSize: 14 } }}
            />
          ) : (
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {isLevelKey && (
                <Tooltip title="Level key column">
                  <KeyIcon fontSize="small" sx={{ color: 'warning.main' }} />
                </Tooltip>
              )}
              <Typography variant="body2" fontWeight={500}>
                {colName}
              </Typography>
            </Stack>
          )}
        </TableCell>

        {/* Type */}
        <TableCell>
          {isEdit ? (
            <FormControl size="small" fullWidth>
              <Select
                value={canonicalizeKnownColumnType(column?.type ?? column?.data_type)}
                onChange={(e) => onColumnPatch?.('type', e.target.value)}
                sx={{ fontSize: 13 }}
              >
                {columnTypeSelectOptions(column?.type ?? column?.data_type ?? 'varchar').map((opt) => (
                  <MenuItem key={opt} value={opt} sx={{ fontSize: 13 }}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip
              label={column?.type || column?.data_type || 'VARCHAR'}
              size="small"
              variant="outlined"
            />
          )}
        </TableCell>

        {/* Constraints + interpretation chips */}
        <TableCell>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
            {hasPrimaryKey && (
              <Chip
                label="PK"
                size="small"
                color="primary"
                onClick={
                  isEdit
                    ? () => onColumnPatch?.('primary_key', !hasPrimaryKey)
                    : undefined
                }
              />
            )}
            {nullableState === false && (
              <Chip
                label="NN"
                size="small"
                color="primary"
                onClick={
                  isEdit
                    ? () => onColumnPatch?.('nullable', true)
                    : undefined
                }
              />
            )}
            {hasUnique && (
              <Chip
                label="UQ"
                size="small"
                color="primary"
                onClick={
                  isEdit
                    ? () => onColumnPatch?.('unique', !hasUnique)
                    : undefined
                }
              />
            )}
            {isEdit && !hasPrimaryKey && (
              <Chip
                label="PK"
                size="small"
                variant="outlined"
                onClick={() => onColumnPatch?.('primary_key', true)}
              />
            )}
            {isEdit && nullableState !== false && (
              <Chip
                label="NN"
                size="small"
                variant="outlined"
                onClick={() => onColumnPatch?.('nullable', false)}
              />
            )}
            {isEdit && !hasUnique && (
              <Chip
                label="UQ"
                size="small"
                variant="outlined"
                onClick={() => onColumnPatch?.('unique', true)}
              />
            )}
            {isAutoGenerated && (
              <Tooltip title="This column is filled in automatically by the platform — you don't need to map it.">
                <Chip
                  icon={<AutoGeneratedIcon />}
                  label="Auto-generated"
                  size="small"
                  sx={{ bgcolor: 'grey.200' }}
                />
              </Tooltip>
            )}
            {isInherited && (
              <Tooltip
                title={
                  ancestor
                    ? `Inherited from ${ancestor}${ancestorSrc ? `.${ancestorSrc}` : ''}`
                    : 'Inherited from a parent record'
                }
              >
                <Chip label="Inherited" size="small" variant="outlined" />
              </Tooltip>
            )}
            {isForeignKey && (
              <Tooltip
                title={
                  referencesParsed
                    ? `Refers to ${referencesParsed.table}.${referencesParsed.column} — each row's value must match a row's ${referencesParsed.column} in ${referencesParsed.table}.`
                    : referencesRaw
                      ? `Refers to ${referencesRaw}`
                      : 'Refers to another table (target not specified).'
                }
              >
                <Chip
                  icon={<LinkIcon />}
                  label={
                    referencesParsed
                      ? `Refers to: ${referencesParsed.table}`
                      : 'Refers to another table'
                  }
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {fixedValue !== null && (
              <Tooltip title={`Always set to "${fixedValue}", regardless of the source.`}>
                <Chip
                  icon={<FixedIcon />}
                  label={`Always "${fixedValue}"`}
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {requiresOperatorValue && fixedValue === null && (
              <Tooltip
                title={
                  'This value isn’t in your data. You’ll be ' +
                  'asked to type it in before we run.'
                }
              >
                <Chip
                  icon={<OperatorIcon />}
                  label="You’ll provide this"
                  size="small"
                  color="warning"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {derivation && (
              <Tooltip
                title={
                  derivation.source_column
                    ? `Auto-filled from "${derivation.source_column}"${
                        derivation.attribute_key
                          ? ` using the linked attribute "${derivation.attribute_key}"`
                          : ''
                      }.`
                    : 'Auto-filled from another column.'
                }
              >
                <Chip
                  icon={<AutoFilledIcon />}
                  label={
                    derivation.source_column
                      ? `Auto-filled from ${derivation.source_column}`
                      : 'Auto-filled'
                  }
                  size="small"
                  sx={{ bgcolor: 'secondary.light', color: 'secondary.dark' }}
                />
              </Tooltip>
            )}
            {scopeParent && (
              <Tooltip title={`Valid values depend on the value of "${scopeParent}".`}>
                <Chip
                  icon={<InfoIcon />}
                  label={`Depends on ${scopeParent}`}
                  size="small"
                  variant="outlined"
                />
              </Tooltip>
            )}
            {enumValues && enumValues.length > 0 && (
              <Tooltip
                title={`Allowed values: ${
                  enumValues.length <= 8
                    ? enumValues.join(', ')
                    : `${enumValues.slice(0, 8).join(', ')}, … (+${enumValues.length - 8} more)`
                }`}
              >
                <Chip
                  icon={<PickListIcon />}
                  label={`Allowed (${enumValues.length})`}
                  size="small"
                  color="success"
                  variant="outlined"
                  onClick={() => setExpanded((v) => !v)}
                  clickable
                />
              </Tooltip>
            )}
            {rangeBounds && (
              <Tooltip
                title={`Allowed range: ${rangeBounds.min} – ${rangeBounds.max}`}
              >
                <Chip
                  icon={<PickListIcon />}
                  label={`Range ${rangeBounds.min}–${rangeBounds.max}`}
                  size="small"
                  color="success"
                  variant="outlined"
                />
              </Tooltip>
            )}
          </Stack>
        </TableCell>

        {/* Description */}
        <TableCell sx={{ minWidth: 220 }}>
          {isEdit ? (
            <TextField
              size="small"
              variant="standard"
              multiline
              maxRows={3}
              value={description}
              onChange={(e) => onColumnPatch?.('description', e.target.value)}
              placeholder="Describe this column"
              fullWidth
            />
          ) : (
            <Typography variant="caption" color="text.secondary">
              {description || '—'}
            </Typography>
          )}
        </TableCell>

        {/* Always set to (edit only) */}
        {isEdit && (
          <TableCell sx={{ minWidth: 130 }}>
            <TextField
              size="small"
              variant="standard"
              value={fixedValue ?? ''}
              onChange={(e) =>
                onColumnPatch?.('fixed', e.target.value === '' ? null : e.target.value)
              }
              placeholder="(optional)"
              fullWidth
            />
          </TableCell>
        )}

        {/* Rules */}
        <TableCell sx={{ width: 96 }}>
          {(() => {
            const rules = Array.isArray(column?.validation_rules) ? column.validation_rules : [];
            const rulesCount = rules.length;
            if (isEdit && onOpenRulesEditor) {
              return (
                <Chip
                  label={`${rulesCount} rules`}
                  size="small"
                  color={rulesCount > 0 ? 'success' : 'default'}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenRulesEditor();
                  }}
                  icon={<EditIcon fontSize="small" />}
                />
              );
            }
            if (rulesCount === 0) {
              return (
                <Typography variant="caption" color="text.secondary">
                  —
                </Typography>
              );
            }
            return (
              <Tooltip
                title={
                  <Box sx={{ maxWidth: 360 }}>
                    {rules.map((rule: any, idx: number) => (
                      <Typography key={idx} variant="caption" display="block">
                        {formatRuleLine(rule)}
                      </Typography>
                    ))}
                  </Box>
                }
                placement="left"
              >
                <Chip label={`${rulesCount}`} size="small" color="success" />
              </Tooltip>
            );
          })()}
        </TableCell>
      </TableRow>

      {/* Inline allowed-values body */}
      {canExpand && (
        <TableRow>
          <TableCell colSpan={isEdit ? 7 : 6} sx={{ p: 0, borderBottom: 'none' }}>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Box sx={{ px: 2, py: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                  Allowed values ({enumValues!.length})
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                  {enumValues!.map((v, idx) => (
                    <Chip key={`${v}-${idx}`} label={v} size="small" variant="outlined" />
                  ))}
                </Stack>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

export default ColumnRowWithCatalog;
